import { createAPIConnection } from 'services';
import { getStore } from 'stores/helpers';
import {
	CreateCustomerParams,
	CreateCustomerResponse,
	ValidateEmailResponse,
	ValidateSignupOTPRequest,
	LoginUserParams,
	LoginUserResponse,
	ResetPasswordParams,
	CustomerDataResponse,
	UpdateCustomerDataParams,
	UpdateCustomerEmailParams,
	ValidateTokenUpdateCustomerEmailParams,
	CreateQuizResponse,
	ValidateQuizResponse,
	ValidateQuizRequest,
	ValidatePartnerVerificationOTPResponse,
	ValidateCustomer2FAResponse,
	GetSerasaStatusResponse,
	GetVerificationOTPRequest,
	ValidateVerificationOTPRequest,
	GetSignupOTPRequest,
	ResetPasswordTokenValidateResponse,
} from './interfaces';
import { requestHandler } from '../helpers';
import { ResponseParams } from '../interfaces';
import { AxiosInstance } from 'axios';
import { loginRateLimitHandler } from './helpers';

let api: AxiosInstance;

import('services').then(({ createAPIConnection }) => {
	api = createAPIConnection('/customers', {
		onRequest(config) {
			const provider = getStore().customerReducer?.provider;

			if (provider !== 'PARCEIRO_HYPERA') Object.assign(config?.headers || {}, { 'Partner-origin': provider });
		},
	});
});

export const createCustomer = async (body: CreateCustomerParams) => {
	return requestHandler(api.post<ResponseParams<CreateCustomerResponse>>('', body));
};

export const loginCustomer = async (body: LoginUserParams) => {
	try {
		const response = await api.post<LoginUserResponse>(
			`/login${window.location.pathname === '/rv' ? '?from=rv' : ''}`,
			body,
			{ baseURL: import.meta.env.VITE_API_BASE },
		);

		return response?.data;
	} catch (error) {
		const { cause } = loginRateLimitHandler((error as any).response);

		throw { cause };
	}
};

export const validateCustomerEmail = async (email: string) => {
	const response = await api.post<ResponseParams<ValidateEmailResponse>>('/validate', {
		email,
	});
	return response?.data?.content;
};

export const createQuiz = async () =>
	requestHandler<CreateQuizResponse>(api.get('/serasa/quiz/create'), {
		throwData: true,
	});

export const validateQuiz = (quiz: ValidateQuizRequest) =>
	requestHandler<ValidateQuizResponse>(
		createAPIConnection('/customers', {
			onReject() {
				throw new Error('Erro ao validar quiz');
			},
		}).post(`/serasa/quiz/result`, quiz),
	);

export const validateCustomerCpf = async (cpf: string) => {
	const response = await api.post<ResponseParams<ValidateEmailResponse>>('/cpf/validate', { cpf });
	return response?.data?.content;
};

export const getUserData = async () => {
	return requestHandler<CustomerDataResponse>(
		api.get('', {
			headers: {
				Authorization: getStore().customerReducer.token!,
			},
		}),
	);
};

export const requestResetPassword = async (email: string) => {
	return requestHandler(api.post('/request/reset/password', { email }));
};

export const resetPassword = async (body: ResetPasswordParams) => {
	return requestHandler(api.post('/reset/password', body));
};

export const updateUserData = async (body: UpdateCustomerDataParams) => {
	return requestHandler(api.put('/update', body), { throwData: true });
};

export const requestUpdateUserEmail = async (body: UpdateCustomerEmailParams) => {
	return requestHandler(api.post('/email/update', body));
};

export const validateTokenUpdateUserEmail = async (body: ValidateTokenUpdateCustomerEmailParams) => {
	return requestHandler(api.post('/email/update/token/validate', body));
};

export const requestPasswordUpdate = async () => {
	return requestHandler(api.post('/password/update', {}));
};

export const validateCustomer2FA = ({ deviceToken, data }: { deviceToken: string; data: Record<string, string> }) =>
	requestHandler<ValidateCustomer2FAResponse>(
		api.get(`/2FA/validate/${deviceToken}`, { headers: { 'X-Fingerprint-Data': JSON.stringify(data) } }),
	);

export const logout = async () => requestHandler(createAPIConnection('/').post('logout'));

export const getSerasaStatus = () =>
	requestHandler<GetSerasaStatusResponse>(api.get('/serasa/status'), { throwData: true });

export function getSignupOTP({ validationKey: Authorization }: GetSignupOTPRequest) {
	return requestHandler(
		api.post('/token/generate', undefined, {
			headers: { Authorization },
		}),
	);
}

export function validateSignupOPT({ validationKey: Authorization, ...payload }: ValidateSignupOTPRequest) {
	return requestHandler(api.post('/token/validate', payload, { headers: { Authorization } }), {
		throwData: true,
	});
}

export function getVerificationOTP(payload: GetVerificationOTPRequest) {
	return requestHandler(api.post('/2FA/token/send', payload));
}

export function validateVerificationOTP(payload: ValidateVerificationOTPRequest) {
	return requestHandler<ValidatePartnerVerificationOTPResponse>(api.post('/2FA/token/validate', payload));
}

export function resetPasswordTokenValidate(payload: { token: string }) {
	return requestHandler<ResetPasswordTokenValidateResponse>(api.post('/reset/password/validate', payload));
}
